/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import api, { setTenantSlug } from '../lib/api'
import { normalizeRole, toLegacyRole } from '../lib/roles'
import { roleHomePath } from '../lib/roleHomePath'
import { ADMIN_TOKEN_BACKUP_KEY, hasAdminTokenBackup, readAdminTokenBackup } from '../lib/impersonationStorage'

export { ADMIN_TOKEN_BACKUP_KEY }

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

const API_TENANT_SLUG_KEY = 'socialbox_api_tenant_slug'
const LEGACY_ACTING_TENANT_KEY = 'socialbox_acting_tenant_slug'

function migrateLegacyTenantSlugStorage() {
  try {
    const legacy = localStorage.getItem(LEGACY_ACTING_TENANT_KEY)
    if (legacy && !localStorage.getItem(API_TENANT_SLUG_KEY)) {
      localStorage.setItem(API_TENANT_SLUG_KEY, legacy)
    }
    if (legacy) localStorage.removeItem(LEGACY_ACTING_TENANT_KEY)
  } catch { /* ignore */ }
}

export const AuthProvider = ({ children }) => {
  const lastProfileRefreshRef = useRef(0)
  const pendingProfileSyncReasonRef = useRef(null)
  const profileSnapshotRef = useRef(null)
  const hasResolvedInitialAuthRef = useRef(false)
  const [sessionSyncNotice, setSessionSyncNotice] = useState(null)

  const clearSessionSyncNotice = useCallback(() => setSessionSyncNotice(null), [])

  const snapshotFromProfile = useCallback((profile, tenantSlug) => {
    if (!profile) return null
    return {
      userId: profile.id,
      role: profile.role_canonical || profile.role,
      tenantSlug: tenantSlug || null,
    }
  }, [])
  const normalizeProfile = useCallback((rawProfile) => {
    if (!rawProfile) return rawProfile
    const canonical = normalizeRole(rawProfile.role, rawProfile.tenant_id)
    const legacy = toLegacyRole(canonical, rawProfile.tenant_id)
    return {
      ...rawProfile,
      role: legacy,
      role_canonical: canonical,
      role_legacy: legacy,
    }
  }, [])

  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [billingEntitlement, setBillingEntitlement] = useState(null)
  const [impersonation, setImpersonation] = useState(null)
  const [loading, setLoading] = useState(true)
  /** X-Tenant header scope during view-as or for tenant-bound account managers. */
  const [apiTenantSlug, setApiTenantSlugState] = useState(null)

  const setApiTenantSlug = useCallback((slug, { persist = false } = {}) => {
    const next = slug ? String(slug).trim() || null : null
    setApiTenantSlugState(next)
    setTenantSlug(next)
    if (!persist) return
    try {
      if (next) localStorage.setItem(API_TENANT_SLUG_KEY, next)
      else localStorage.removeItem(API_TENANT_SLUG_KEY)
    } catch { /* ignore */ }
  }, [])

  /**
   * @param {{ blocking?: boolean }} [options]
   * blocking=true shows the global auth spinner (initial load only).
   * Background refreshes (focus/storage) must stay non-blocking so modals and forms keep state.
   */
  const fetchUserProfile = useCallback(async (options = {}) => {
    const blocking = options.blocking === true
      || (options.blocking !== false && !hasResolvedInitialAuthRef.current)
    try {
      if (blocking) setLoading(true)
      const data = await api.auth.getMe()
      const profile = normalizeProfile({ ...data.user, tenant_admin_for: data.tenant_admin_for ?? [] })
      setUser({ id: profile.id, email: profile.email })
      setUserProfile(profile)
      setBillingEntitlement(data.billing_entitlement || null)
      const impersonationPayload = data.impersonation?.active ? data.impersonation : null
      if (!impersonationPayload && hasAdminTokenBackup()) {
        try {
          sessionStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY)
        } catch { /* ignore */ }
      }
      setImpersonation(impersonationPayload)
      const isImpersonating = impersonationPayload?.active === true
      if (isImpersonating) {
        // Tenant slug for X-Tenant is set by startImpersonation.
      } else if (profile.role_canonical === 'super_admin' && !profile.tenant_id) {
        setApiTenantSlug(null)
        try {
          localStorage.removeItem(API_TENANT_SLUG_KEY)
        } catch { /* ignore */ }
      } else if (profile.role_canonical === 'account_manager' && profile.tenant_id && profile.tenant_admin_for?.length > 0) {
        const slug = profile.tenant_admin_for[0]?.slug ?? null
        setApiTenantSlug(slug, { persist: true })
      } else if ((profile.role_canonical !== 'account_manager' && profile.role_canonical !== 'super_admin') || profile.tenant_id) {
        setTenantSlug(null)
        setApiTenantSlugState(null)
        try {
          localStorage.removeItem(API_TENANT_SLUG_KEY)
        } catch { /* ignore */ }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
      setUser(null)
      setUserProfile(null)
      setBillingEntitlement(null)
      setImpersonation(null)
      setTenantSlug(null)
      setApiTenantSlugState(null)
      localStorage.removeItem('auth_token')
    } finally {
      hasResolvedInitialAuthRef.current = true
      if (blocking) setLoading(false)
    }
  }, [normalizeProfile, setApiTenantSlug])

  const refreshProfileIfNeeded = useCallback((reason = 'unknown') => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      pendingProfileSyncReasonRef.current = reason
      setUser(null)
      setUserProfile(null)
      setBillingEntitlement(null)
      setImpersonation(null)
      setTenantSlug(null)
      setApiTenantSlugState(null)
      return
    }
    const now = Date.now()
    // Avoid duplicate calls from focus + visibility + storage bursts.
    if (now - lastProfileRefreshRef.current < 1500) return
    lastProfileRefreshRef.current = now
    pendingProfileSyncReasonRef.current = reason
    fetchUserProfile()
  }, [fetchUserProfile])

  useEffect(() => {
    if (loading) return

    const reason = pendingProfileSyncReasonRef.current
    if (reason) pendingProfileSyncReasonRef.current = null

    const prev = profileSnapshotRef.current
    const next = snapshotFromProfile(userProfile, apiTenantSlug)
    profileSnapshotRef.current = next

    if (!reason || !prev) return
    // Only notify on cross-tab auth changes, not when returning to this tab.
    if (reason !== 'storage') return

    const identityChanged =
      (!next && prev)
      || (next && !prev)
      || prev.userId !== next?.userId
      || prev.role !== next?.role

    if (!identityChanged) return

    if (!userProfile) {
      setSessionSyncNotice({
        message: 'You were signed out in another browser window. Sign in here if you still need access.',
      })
      return
    }

    setSessionSyncNotice({
      message: 'Your account was updated in another browser window. Use the menu if this page looks wrong.',
    })
  }, [loading, userProfile, apiTenantSlug, snapshotFromProfile])

  useEffect(() => {
    migrateLegacyTenantSlugStorage()
    const token = localStorage.getItem('auth_token')
    if (token) {
      fetchUserProfile({ blocking: true })
    } else {
      hasResolvedInitialAuthRef.current = true
      setLoading(false)
    }
  }, [fetchUserProfile])

  useEffect(() => {
    const onStorage = (event) => {
      if (!event) return
      if (event.key !== 'auth_token' && event.key !== API_TENANT_SLUG_KEY) return
      refreshProfileIfNeeded('storage')
    }

    const onFocus = () => refreshProfileIfNeeded('focus')
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshProfileIfNeeded('visibility')
      }
    }

    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [refreshProfileIfNeeded])

  const signIn = useCallback(async (email, password) => {
    clearSessionSyncNotice()
    setTenantSlug(null)
    setApiTenantSlugState(null)
    try {
      localStorage.removeItem('auth_token')
      sessionStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY)
    } catch { /* ignore */ }
    try {
      const data = await api.auth.login(email, password)
      const profile = normalizeProfile({ ...data.user, tenant_admin_for: data.tenant_admin_for ?? [] })
      setUser({ id: profile.id, email: profile.email })
      setUserProfile(profile)
      setBillingEntitlement(data.billing_entitlement || null)
      if (profile.role_canonical === 'super_admin' && !profile.tenant_id) {
        setApiTenantSlug(null)
        try {
          localStorage.removeItem(API_TENANT_SLUG_KEY)
        } catch { /* ignore */ }
      } else if (profile.role_canonical === 'account_manager' && profile.tenant_id && profile.tenant_admin_for?.length > 0) {
        const slug = profile.tenant_admin_for[0]?.slug ?? null
        setApiTenantSlug(slug, { persist: true })
      } else if ((profile.role_canonical !== 'account_manager' && profile.role_canonical !== 'super_admin') || profile.tenant_id) {
        setTenantSlug(null)
        setApiTenantSlugState(null)
        try {
          localStorage.removeItem(API_TENANT_SLUG_KEY)
        } catch { /* ignore */ }
      }
      return { error: null, data }
    } catch (error) {
      const msg = String(error?.message || '').toLowerCase()
      const isExpectedInvalidLogin = error?.status === 401 || msg.includes('unauthorized') || msg.includes('invalid credentials')
      if (!isExpectedInvalidLogin) {
        console.error('Sign in error:', error)
      }
      return { error }
    }
  }, [normalizeProfile, setApiTenantSlug, clearSessionSyncNotice])

  const signOut = useCallback(async () => {
    clearSessionSyncNotice()
    // After view-as, Sign out should restore the admin session instead of discarding the backup token.
    if (hasAdminTokenBackup()) {
      const backup = readAdminTokenBackup()
      try {
        sessionStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY)
      } catch { /* ignore */ }
      if (backup) {
        localStorage.setItem('auth_token', backup)
        setTenantSlug(null)
        setApiTenantSlugState(null)
        try {
          localStorage.removeItem(API_TENANT_SLUG_KEY)
        } catch { /* ignore */ }
        await fetchUserProfile({ blocking: false })
        return
      }
    }
    try {
      await api.auth.logout()
    } catch (error) {
      console.error('Sign out error:', error)
    } finally {
      setUser(null)
      setUserProfile(null)
      setBillingEntitlement(null)
      setImpersonation(null)
      setTenantSlug(null)
      setApiTenantSlugState(null)
      localStorage.removeItem('auth_token')
      try {
        localStorage.removeItem(API_TENANT_SLUG_KEY)
        sessionStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY)
      } catch { /* ignore */ }
    }
  }, [fetchUserProfile, clearSessionSyncNotice])

  const startImpersonation = useCallback(async (targetUserId) => {
    clearSessionSyncNotice()
    const adminToken = localStorage.getItem('auth_token')
    const data = await api.platform.impersonate(targetUserId)
    try {
      if (adminToken) sessionStorage.setItem(ADMIN_TOKEN_BACKUP_KEY, adminToken)
      else sessionStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY)
    } catch { /* ignore */ }
    if (data.token) {
      localStorage.setItem('auth_token', data.token)
    }
    if (data.tenant_slug) setApiTenantSlug(data.tenant_slug)
    else setApiTenantSlug(null)
    await fetchUserProfile({ blocking: false })
    return roleHomePath(normalizeProfile(data.user))
  }, [fetchUserProfile, setApiTenantSlug, normalizeProfile, clearSessionSyncNotice])

  const endImpersonation = useCallback(async () => {
    clearSessionSyncNotice()
    try {
      await api.auth.endImpersonation()
    } catch {
      // Session may already be expired.
    }
    const backup = readAdminTokenBackup()
    try {
      sessionStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY)
    } catch { /* ignore */ }
    if (backup) localStorage.setItem('auth_token', backup)
    else localStorage.removeItem('auth_token')
    setApiTenantSlug(null)
    await fetchUserProfile({ blocking: false })
  }, [fetchUserProfile, setApiTenantSlug, clearSessionSyncNotice])

  useEffect(() => {
    const onExpired = () => {
      endImpersonation()
    }
    window.addEventListener('socialbox:impersonation-expired', onExpired)
    return () => window.removeEventListener('socialbox:impersonation-expired', onExpired)
  }, [endImpersonation])

  const isViewingAsUser = impersonation?.active === true

  const value = useMemo(
    () => ({
      user,
      userProfile,
      billingEntitlement,
      loading,
      signIn,
      signOut,
      fetchUserProfile,
      apiTenantSlug,
      setApiTenantSlug,
      impersonation,
      isViewingAsUser,
      startImpersonation,
      endImpersonation,
      sessionSyncNotice,
      clearSessionSyncNotice,
    }),
    [user, userProfile, billingEntitlement, loading, signIn, signOut, fetchUserProfile, apiTenantSlug, setApiTenantSlug, impersonation, isViewingAsUser, startImpersonation, endImpersonation, sessionSyncNotice, clearSessionSyncNotice]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
